import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function ProjectDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);

  useEffect(() => { api.get(`/projects/${id}`).then(r => setP(r.data)); }, [id]);

  if (!p) return <div className="p-8 text-slate-500">Loading…</div>;

  const statuses = ["todo", "in_progress", "review", "done"];
  const byStatus = statuses.reduce((acc, s) => { acc[s] = p.tasks.filter(t => t.status === s); return acc; }, {});

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="project-detail">
      <Card className="p-6 border-slate-200 shadow-none">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Project</div>
            <h1 className="text-2xl font-[Outfit] font-semibold text-slate-900">{p.name}</h1>
            <p className="text-sm text-slate-600 mt-1">{p.description}</p>
          </div>
          <Badge className="bg-emerald-100 text-emerald-700 border-0 capitalize">{p.status.replace("_"," ")}</Badge>
        </div>
        <div className="mt-5 grid md:grid-cols-4 gap-4">
          <div><div className="text-xs uppercase text-slate-500">Progress</div><Progress value={p.progress} className="h-1.5 mt-2"/><div className="text-sm font-semibold mt-1">{p.progress}%</div></div>
          <div><div className="text-xs uppercase text-slate-500">Tasks</div><div className="text-2xl font-[Outfit] font-semibold mt-1">{p.tasks.length}</div></div>
          <div><div className="text-xs uppercase text-slate-500">Members</div><div className="text-2xl font-[Outfit] font-semibold mt-1">{p.members_detail?.length || 0}</div></div>
          <div><div className="text-xs uppercase text-slate-500">Due</div><div className="text-sm font-semibold mt-2">{p.end_date ? new Date(p.end_date).toLocaleDateString() : "—"}</div></div>
        </div>
      </Card>

      <Tabs defaultValue="board">
        <TabsList className="bg-slate-100"><TabsTrigger value="board">Board</TabsTrigger><TabsTrigger value="members">Members</TabsTrigger></TabsList>
        <TabsContent value="board" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {statuses.map((s) => (
              <div key={s}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600 capitalize">{s.replace("_"," ")}</div>
                  <Badge variant="outline">{byStatus[s].length}</Badge>
                </div>
                <div className="space-y-2">
                  {byStatus[s].map((t) => (
                    <Card key={t.id} className="p-3 border-slate-200 shadow-none">
                      <div className="text-sm font-medium text-slate-900">{t.title}</div>
                      <div className="mt-2 text-xs text-slate-500 capitalize">{t.priority} priority</div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="members" className="mt-4">
          <Card className="p-5 border-slate-200 shadow-none">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {p.members_detail?.map((m) => (
                <Link key={m.id} to={`/people/${m.id}`} className="flex items-center gap-3 p-3 rounded-md bg-slate-50 hover:bg-slate-100">
                  <Avatar className="w-9 h-9"><AvatarFallback className="bg-slate-900 text-white text-xs">
                    {m.full_name.split(" ").map(s => s[0]).slice(0,2).join("")}
                  </AvatarFallback></Avatar>
                  <div>
                    <div className="text-sm font-medium text-slate-900">{m.full_name}</div>
                    <div className="text-xs text-slate-500">{m.designation}</div>
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
